import { NodeModel } from "@minoru/react-dnd-treeview";
import React, { useMemo, useRef, useState } from "react";
import { TagFromTree } from "../models/TrendingModelsV2";

export interface TagSearchResult {
	id: number;
	name: string;
	alias: string;
	path: string;
	isDroppable: boolean;
	hasChildren: boolean;
	tagTypeCode: string;
	node: NodeModel<TagFromTree>;
}

interface UseTagSearchProps {
	tagNodes: NodeModel<TagFromTree>[];
	externalSearchTerm?: string;
	onSearchTermChange?: (term: string) => void;
}

export const useTagSearch = ({
	tagNodes,
	externalSearchTerm,
	onSearchTermChange,
}: UseTagSearchProps) => {
	const [searchTerm, setSearchTerm] = useState(externalSearchTerm || "");
	const [searchResults, setSearchResults] = useState<TagSearchResult[]>([]);
	const [showSearchResults, setShowSearchResults] = useState(false);
	const [focusedSearchIndex, setFocusedSearchIndex] = useState(-1);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchResultsRefs = useRef<(HTMLElement | null)[]>([]);

	const searchIndex = useMemo((): TagSearchResult[] => {
		const index: TagSearchResult[] = [];

		const nodeMap = new Map<number, NodeModel<TagFromTree>>();
		tagNodes.forEach((node) => {
			if (node.id !== undefined && node.id !== null) {
				nodeMap.set(node.id as number, node);
			}
		});

		const buildNodePath = (nodeId: number): string[] => {
			const path: string[] = [];
			let currentNode = nodeMap.get(nodeId);

			while (currentNode && currentNode.parent !== 1000000) {
				path.unshift(currentNode.text);
				currentNode = nodeMap.get(currentNode.parent as number);
			}

			if (currentNode && currentNode.parent === 1000000) {
				path.unshift(currentNode.text);
			}

			return path;
		};

		tagNodes.forEach((node) => {
			if (node.id === undefined || node.id === null || !node.text) return;

			// Skip folder nodes - only include actual tags
			if (node.data?.TagTypeCode === "F") return;

			const nodePath = buildNodePath(node.id as number);
			const pathStr = nodePath.join(" > ");

			const hasChildren = tagNodes.some((child) => child.parent === node.id);

			index.push({
				id: node.id as number,
				name: node.text,
				alias: node.data?.Alias || "",
				path: pathStr,
				isDroppable: node.droppable || false,
				hasChildren: hasChildren,
				tagTypeCode: node.data?.TagTypeCode || "",
				node: node,
			});
		});

		return index;
	}, [tagNodes]);

	const searchNodes = (searchTerm: string): TagSearchResult[] => {
		const lowerSearchTerm = searchTerm.toLowerCase();
		return searchIndex.filter(
			(item) =>
				item.name.toLowerCase().includes(lowerSearchTerm) ||
				item.alias.toLowerCase().includes(lowerSearchTerm),
		);
	};

	React.useEffect(() => {
		if (externalSearchTerm !== undefined && externalSearchTerm !== searchTerm) {
			setSearchTerm(externalSearchTerm);
		}
	}, [externalSearchTerm]);

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const term = event.target.value;
		setSearchTerm(term);
		onSearchTermChange?.(term);

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		if (term.trim() === "" || term.length < 2) {
			setSearchResults([]);
			setShowSearchResults(false);
			setFocusedSearchIndex(-1);
			return;
		}

		searchTimeoutRef.current = setTimeout(() => {
			const results = searchNodes(term);
			setSearchResults(results);
			setShowSearchResults(results.length > 0);
			const newFocusIndex = results.length > 0 ? 0 : -1;
			setFocusedSearchIndex(newFocusIndex);
			searchResultsRefs.current = new Array(results.length).fill(null);
		}, 250);
	};

	const handleKeyDown = (
		event: React.KeyboardEvent<HTMLDivElement>,
	): TagSearchResult | undefined => {
		if (event.key === "Escape") {
			if (showSearchResults) {
				setSearchTerm("");
				setSearchResults([]);
				setShowSearchResults(false);
				setFocusedSearchIndex(-1);
			}
			return undefined;
		}

		if (showSearchResults && searchResults.length > 0) {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				const newIndex =
					focusedSearchIndex < searchResults.length - 1
						? focusedSearchIndex + 1
						: 0;
				setFocusedSearchIndex(newIndex);
				setTimeout(() => scrollFocusedItemIntoView(newIndex), 0);
			} else if (event.key === "ArrowUp") {
				event.preventDefault();
				const newIndex =
					focusedSearchIndex > 0
						? focusedSearchIndex - 1
						: searchResults.length - 1;
				setFocusedSearchIndex(newIndex);
				setTimeout(() => scrollFocusedItemIntoView(newIndex), 0);
			} else if (event.key === "Enter" && focusedSearchIndex >= 0) {
				event.preventDefault();
				const selectedResult = searchResults[focusedSearchIndex];
				return selectedResult;
			}
		}

		return undefined;
	};

	const scrollFocusedItemIntoView = (index: number) => {
		if (searchResultsRefs.current[index]) {
			searchResultsRefs.current[index]?.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	};

	const clearSearch = () => {
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
		setFocusedSearchIndex(-1);
		onSearchTermChange?.("");
	};

	const triggerSearch = (term: string) => {
		setSearchTerm(term);
		onSearchTermChange?.(term);

		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		if (term.trim() === "" || term.length < 2) {
			setSearchResults([]);
			setShowSearchResults(false);
			setFocusedSearchIndex(-1);
			return;
		}

		searchTimeoutRef.current = setTimeout(() => {
			const results = searchNodes(term);
			setSearchResults(results);
			setShowSearchResults(results.length > 0);
			const newFocusIndex = results.length > 0 ? 0 : -1;
			setFocusedSearchIndex(newFocusIndex);
			searchResultsRefs.current = new Array(results.length).fill(null);
		}, 250);
	};

	return {
		searchTerm,
		searchResults,
		showSearchResults,
		focusedSearchIndex,
		searchResultsRefs,
		handleSearchChange,
		handleKeyDown,
		scrollFocusedItemIntoView,
		clearSearch,
		triggerSearch,
	};
};
